/**
 * UsageSection — the old "שימוש" tab folded into the overview:
 * current month, SMS by source, AI by feature, all-time, per-therapist SMS,
 * monthly history.
 */

import { Brain, Cpu, PenNib, ChatCircleDots, VideoCamera, ChartLineUp } from "@phosphor-icons/react";
import { SMS_SOURCES, buildAiBreakdownRows, getAiFunctionLabel, formatLimit } from "../clinicDetailConstants";
import { isUnlimited } from "../../../../../services/planLimits";

function SmsSourceBadges({ bySource, compact = false }) {
  const items = SMS_SOURCES.filter((s) => (bySource?.[s.key] || 0) > 0);
  if (items.length === 0) return <span className="ncd2-muted">—</span>;
  return (
    <div className={`ncd2-badges${compact ? " ncd2-badges-compact" : ""}`}>
      {items.map((s) => (
        <span key={s.key} className={`ncd2-badge ncd2-badge-${s.tone}`}>
          {s.label}: {bySource[s.key]}
        </span>
      ))}
    </div>
  );
}

function AiFunctionBadges({ byFunction }) {
  const rows = Object.entries(byFunction || {})
    .filter(([, v]) => (v?.tokens || 0) > 0 || (v?.requests || 0) > 0)
    .sort(([, a], [, b]) => (b?.tokens || 0) - (a?.tokens || 0));
  if (rows.length === 0) return <span className="ncd2-muted">—</span>;
  return (
    <div className="ncd2-badges ncd2-badges-compact">
      {rows.map(([key, v]) => (
        <span key={key} className="ncd2-badge ncd2-badge-ai" title={`${v.requests || 0} בקשות`}>
          {getAiFunctionLabel(key)}: {(v.tokens || 0).toLocaleString("he-IL")}
        </span>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, value, label, limit }) {
  return (
    <div className="ncd2-stat">
      <span className="ncd2-card-icon">
        <Icon size={14} weight="bold" />
      </span>
      <div className="ncd2-stat-info">
        <span className="ncd2-stat-value">
          {value}
          {limit != null && <span className="ncd2-stat-limit">/{limit}</span>}
        </span>
        <span className="ncd2-stat-label">{label}</span>
      </div>
    </div>
  );
}

export default function UsageSection({ clinic }) {
  const eff = clinic.effectiveLimits || {};
  const aiRows = buildAiBreakdownRows(clinic.currentMonthAiByFunction, clinic.totalAiByFunction);

  return (
    <section className="ncd2-card ncd2-usage">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <ChartLineUp size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">AI · SMS · חתימות · וידאו</span>
          <h3 className="ncd2-card-title">שימוש</h3>
        </div>
      </header>

      <h4 className="ncd2-subhead">חודש נוכחי</h4>
      <div className="ncd2-stat-grid">
        <Stat
          icon={ChatCircleDots}
          value={(clinic.currentMonthSmsSent || 0).toLocaleString("he-IL")}
          limit={formatLimit(eff.smsPerMonth)}
          label="הודעות SMS"
        />
        <Stat
          icon={Brain}
          value={(clinic.currentMonthAiRequests || 0).toLocaleString("he-IL")}
          limit={isUnlimited(eff.aiActionsPerMonth) ? "∞" : formatLimit(eff.aiActionsPerMonth)}
          label="בקשות AI"
        />
        <Stat icon={Cpu} value={(clinic.currentMonthAiTokens || 0).toLocaleString("he-IL")} label="טוקנים AI" />
        <Stat icon={PenNib} value={clinic.currentMonthDigitalSignatures || 0} label="חתימות דיגיטליות" />
        <Stat
          icon={VideoCamera}
          value={clinic.currentMonthVideoMinutes || 0}
          limit={eff.videoMinutesPerMonth}
          label={`דקות וידאו (${clinic.currentMonthVideoSessions || 0} שיחות)`}
        />
      </div>

      {(clinic.currentMonthSmsSent || 0) > 0 && (
        <div className="ncd2-usage-row">
          <span className="ncd2-usage-row-label">SMS לפי מקור</span>
          <SmsSourceBadges bySource={clinic.currentMonthSmsBySource} />
        </div>
      )}

      {aiRows.length > 0 && (
        <>
          <h4 className="ncd2-subhead">AI לפי פיצ'ר</h4>
          <div className="ncd2-table-wrap">
            <table className="ncd2-table">
              <thead>
                <tr>
                  <th>פיצ'ר</th>
                  <th>בקשות החודש</th>
                  <th>טוקנים החודש</th>
                  <th>בקשות סה"כ</th>
                  <th>טוקנים סה"כ</th>
                </tr>
              </thead>
              <tbody>
                {aiRows.map((r) => (
                  <tr key={r.key}>
                    <td data-label="פיצ'ר">
                      <span className="ncd2-table-primary">{r.label}</span>
                      <span className="ncd2-table-key">{r.key}</span>
                    </td>
                    <td data-label="בקשות החודש">{r.monthRequests}</td>
                    <td data-label="טוקנים החודש">
                      <strong>{r.monthTokens.toLocaleString("he-IL")}</strong>
                    </td>
                    <td data-label='בקשות סה"כ'>{r.totalRequests}</td>
                    <td data-label='טוקנים סה"כ'>{r.totalTokens.toLocaleString("he-IL")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h4 className="ncd2-subhead">סה"כ כל הזמנים</h4>
      <div className="ncd2-stat-grid">
        <Stat icon={ChatCircleDots} value={(clinic.totalSmsSent || 0).toLocaleString("he-IL")} label="הודעות SMS" />
        <Stat icon={Brain} value={(clinic.totalAiRequests || 0).toLocaleString("he-IL")} label="בקשות AI" />
        <Stat icon={Cpu} value={(clinic.totalAiTokens || 0).toLocaleString("he-IL")} label="טוקנים AI" />
        <Stat icon={PenNib} value={clinic.totalDigitalSignatures || 0} label="חתימות דיגיטליות" />
        <Stat icon={VideoCamera} value={clinic.totalVideoMinutes || 0} label="דקות וידאו" />
      </div>

      {clinic.therapistSmsData?.length > 0 && (
        <>
          <h4 className="ncd2-subhead">SMS לפי מטפל</h4>
          <div className="ncd2-table-wrap">
            <table className="ncd2-table">
              <thead>
                <tr>
                  <th>מטפל</th>
                  <th>החודש</th>
                  <th>סה"כ</th>
                  <th>פירוט חודשי</th>
                </tr>
              </thead>
              <tbody>
                {clinic.therapistSmsData.map((t) => (
                  <tr key={t.therapistId}>
                    <td data-label="מטפל">
                      <span className="ncd2-table-primary">{t.fullName || t.therapistId}</span>
                    </td>
                    <td data-label="החודש">
                      <strong>{t.smsSentCountThisMonth}</strong>
                    </td>
                    <td data-label='סה"כ'>{t.smsSentCountAllTime}</td>
                    <td data-label="פירוט">
                      <SmsSourceBadges bySource={t.smsBySourceThisMonth} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {clinic.usageHistory?.length > 0 && (
        <>
          <h4 className="ncd2-subhead">היסטוריה חודשית</h4>
          <div className="ncd2-table-wrap">
            <table className="ncd2-table">
              <thead>
                <tr>
                  <th>חודש</th>
                  <th>SMS</th>
                  <th>בקשות AI</th>
                  <th>טוקנים</th>
                  <th>חתימות</th>
                  <th>וידאו (דק׳)</th>
                  <th>AI לפי פיצ'ר</th>
                </tr>
              </thead>
              <tbody>
                {clinic.usageHistory.map((u) => (
                  <tr key={u.month}>
                    <td data-label="חודש">
                      <span className="ncd2-table-primary" dir="ltr">{u.month}</span>
                    </td>
                    <td data-label="SMS">{u.smsSentCount || 0}</td>
                    <td data-label="בקשות AI">{u.aiRequestsCount}</td>
                    <td data-label="טוקנים">{(u.aiTokensUsed || 0).toLocaleString("he-IL")}</td>
                    <td data-label="חתימות">{u.digitalSignatureCount}</td>
                    <td data-label="וידאו">{u.videoMeetingMinutes || 0}</td>
                    <td data-label="AI לפי פיצ'ר">
                      <AiFunctionBadges byFunction={u.aiUsageByFunction} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
